import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConferenceTypeEnum } from '@matap/shared';
import { Animated, PanResponder, ViewStyle, GestureResponderEvent, PanResponderGestureState } from 'react-native';
import styled from 'styled-components/native';
import { useAppDispatch } from '@/hooks/use.redux';
import { actionSetPIP } from '@/redux';
import { AppRTCView } from './app.rtc.view';
import { P2PRoom } from '@/webrtc/p2p.room';
import { P2PPeer } from '@/webrtc/p2p.peer';
import { hpVal, minVal } from '../../../../utils';

interface Props {
  pip?: boolean;
  style?: ViewStyle;
  connection: P2PRoom;
}
function BaseRemoteView({ pip, connection, style }: Props) {
  const [stream, setStream] = useState(undefined as MediaStream | undefined);

  useEffect(() => {
    const streamListener = (stream?: MediaStream) => {
      setStream(stream);
    };
    const peersListener = (peers: P2PPeer[]) => {
      peers.forEach((peer) => {
        peer.getPeerRemoteStream().on('change', streamListener);
        streamListener(peer.getPeerRemoteStream().stream());
      });
    };
    peersListener(connection.getPeers());
    connection.on('peers', peersListener);

    return () => {
      connection.off('peers', peersListener);
      connection.getPeers().forEach((peer) => peer.getPeerRemoteStream().off('change', streamListener));
    };
  }, [connection]);

  return pip ? (
    <PIPView connection={connection} stream={stream} style={style} />
  ) : (
    <AppRTCView
      deviceManager={connection.getDeviceManager()}
      zIndex={0}
      src={stream}
      objectFit={'cover'}
      style={{ ...style, ...(connection?.type === ConferenceTypeEnum.audio ? { width: 1, height: 1 } : {}) }}
    />
  );
}

function PIPView({ connection, stream, style }: { connection: P2PRoom; style?: ViewStyle; stream: MediaStream }) {
  const pan = useMemo(() => new Animated.ValueXY(), []);
  const dispatch = useAppDispatch();
  const event = useMemo(() => Animated.event([null, { dx: pan.x, dy: pan.y }], { useNativeDriver: false }), [pan.x, pan.y]);

  // FIX: Use a stable mutable object instead of useRef to appease the strict compiler
  const tapTracker = useRef(0);

  const handlePanStart = useCallback(() => {
    tapTracker.current = Date.now();
  }, [tapTracker]);

  const handlePanRelease = useCallback(() => {
    const isTap = tapTracker.current > 0 && Date.now() - tapTracker.current < 100;
    if (isTap) {
      dispatch(actionSetPIP(false));
    }
    // Reset tracker to prevent ghost taps
    tapTracker.current = 0;
  }, [dispatch, tapTracker]);

  const handlePanMove = useCallback(
    (e: GestureResponderEvent, gestureState: PanResponderGestureState) => {
      event(e, gestureState);
    },
    [event],
  );

  const panResponder = useMemo(() => {
    return PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderGrant: () => pan.extractOffset(),
      onPanResponderTerminationRequest: () => false,
      onPanResponderStart: handlePanStart,
      onPanResponderRelease: handlePanRelease,
      onPanResponderMove: handlePanMove,
    });
  }, [pan, handlePanStart, handlePanRelease, handlePanMove]);

  return (
    <PIPAnimated
      {...panResponder.panHandlers}
      style={{
        transform: pan.getTranslateTransform(),
        display: connection.type === ConferenceTypeEnum.audio ? 'none' : 'flex',
        ...style,
      }}
    >
      <FullRTCView deviceManager={connection.getDeviceManager()} zIndex={0} src={stream} objectFit={'cover'} />
    </PIPAnimated>
  );
}

const PIPAnimated = styled(Animated.View)`
  border-radius: 5px;
  z-index: 1000;
  width: ${minVal((50 * 3) / 4)};
  height: ${minVal(50)};
  max-width: 350px;
  max-height: 450px;
  position: absolute;
  top: ${hpVal(16)};
  left: ${hpVal(1)};
`;

const FullRTCView = styled(AppRTCView)`
  width: 100%;
  height: 100%;
  border-radius: 5px;
`;

export const RemoteView = React.memo(BaseRemoteView);
